import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import Tabs from './Tabs';
import EditFormsPreConsult from './EditFormsPreConsult';
import EditAnamnese from './EditAnamnese';
import EditAntropometria from './EditAntropometria';
import EditEvolucao from './EditEvolucao';
import EditConsultPlanoAlimentar from './EditConsultPlanoAlimentar';
import EditSolicitacaoExames from './EditSolicitacaoExames';
import { useConsulta } from '../contexts/ConsultaContext';
import { alertModal } from '../utils/modalController';
import { fetchAuth } from '../utils/fetchAuth';

const apiUrl = process.env.REACT_APP_API_URL || '';
interface Props {
    pacienteId: string;
    consultaId?: string;
}

const EditConsult: React.FC<Props> = ({ pacienteId, consultaId }) => {
    const { consulta, setConsulta } = useConsulta();
    const [actualTab, setActualTab] = useState('pre');
    const [unsalvedTabs, setUnsalvedTabs] = useState<string[]>([]);

    useEffect(() => {
        const fetchConsulta = async () => {
            if (!consultaId) return;

            fetchAuth(`${apiUrl}/consultorio/consultations/${consultaId}`)
                .then(async (response) => {
                    if (!response.ok) {
                        const error_json = await response.json();
                        throw new Error(error_json.message || 'Erro ao buscar consulta');
                    }
                    return response.json();
                })
                .then(data => {
                    setConsulta(
                        {
                            ...data,
                            anamnese: data ? (data.anamnese ?
                                {
                                    ...data.anamnese,
                                    last_updated: (new Date()).toISOString(),
                                    last_edited: (new Date()).toISOString(),
                                }
                                : null) : null,
                            avaliacao_antropometrica: data ? (data.avaliacao_antropometrica ?
                                {
                                    ...data.avaliacao_antropometrica,
                                    last_updated: (new Date()).toISOString(),
                                    last_edited: (new Date()).toISOString(),
                                } : null) : null,
                            evolucao: data ? (data.evolucao ?
                                {
                                    ...data.evolucao,
                                    last_updated: (new Date()).toISOString(),
                                    last_edited: (new Date()).toISOString(),
                                } : null) : null,
                            solicitacao_exames: data ? (data.solicitacao_exames ?
                                {
                                    ...data.solicitacao_exames,
                                    last_updated: (new Date()).toISOString(),
                                    last_edited: (new Date()).toISOString(),
                                } : null) : null,
                            last_updated: data ? (data.last_updated || (new Date()).toISOString()) : null,
                            last_edited: data ? (data.last_edited || (new Date()).toISOString()) : null,
                        }
                    );
                })
                .catch(error => {
                    console.error('Erro ao buscar consulta:', error);
                    alertModal('Erro ao buscar consulta: ' + error.message);
                    setConsulta(null);
                });
        };

        fetchConsulta();
    }, [consultaId, setConsulta, pacienteId]);

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (unsalvedTabs.includes(actualTab)) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [actualTab, unsalvedTabs]);

    useEffect(() => {
        if (consulta) {
            const unsaved: string[] = [];

            const isModified = (subsec?: { last_edited?: string, last_updated?: string }) => {
                if (!subsec?.last_edited || !subsec?.last_updated) return false;
                return new Date(subsec.last_edited) > new Date(subsec.last_updated);
            };

            if (isModified(consulta.anamnese)) {
                unsaved.push('anamnese');
            }
            if (isModified(consulta.avaliacao_antropometrica)) {
                unsaved.push('av');
            }
            if (isModified(consulta.evolucao)) {
                unsaved.push('evolucao');
            }
            if (isModified(consulta.solicitacao_exames)) {
                unsaved.push('exames');
            }

            setUnsalvedTabs(unsaved);
        } else {
            setUnsalvedTabs([]);
        }
    }, [consulta, actualTab]);

    return (
        <Tabs
            guias={[
                { key: 'pre', titulo: 'Questionários Pré-Consulta', conteudo: <EditFormsPreConsult pacienteId={pacienteId} consultaId={consultaId} /> },
                { key: 'anamnese', titulo: 'Anamnese', conteudo: <EditAnamnese /> },
                { key: 'av', titulo: 'Avaliação Antropométrica', conteudo: <EditAntropometria /> },
                { key: 'evolucao', titulo: 'Evolução', conteudo: <EditEvolucao /> },
                { key: 'plano', titulo: 'Plano Alimentar', conteudo: <EditConsultPlanoAlimentar pacienteId={pacienteId} consultaId={consultaId} /> },
                { key: 'exames', titulo: 'Solicitação de Exames', conteudo: <EditSolicitacaoExames /> },
            ]}
            confirmRequired={unsalvedTabs.includes(actualTab)}
            onTabChange={(newKey) => {
                setActualTab(newKey);
            }}
            defaultKey={actualTab}
        />
    );
};

export default EditConsult;
